<template>
  <div class="pablo-dashboard">
    
    <header class="header">
      <h1 class="page-heading">PABLO</h1>
      <div class="subtitle">Planning • Analysis • Buying • Logistics • Operations</div>
    </header>

    <main class="menu">
      <!-- Each row animates in, one at a time -->
      <section
        v-for="(row, idx) in filteredRows"
        :key="row.key"
        class="row"
        :class="{ open: openKey === row.key }"
        :style="{ animationDelay: `${idx * 80}ms` }"
      >

        <!-- Clickable row header -->
        <button class="row-header" type="button" @click="toggleRow(row.key)">
          <span class="big-letter">{{ row.letter }}</span>
          <span class="word">{{ row.word }}</span>

          <span class="chev" aria-hidden="true">
            {{ openKey === row.key ? "▾" : "▸" }}
          </span>
        </button>

        <!-- Options that expand/collapse -->
        <transition name="expand">
          <div v-if="openKey === row.key" class="row-body">
            <div class="option-grid">
              <router-link
                v-for="opt in row.options"
                :key="opt.label"
                class="option"
                :to="opt.to"
              >
                {{ opt.label }}
              </router-link>
            </div>
          </div>
        </transition>
      </section>
    </main>
  </div>
</template>

<script>
export default {
  name: "DashboardPage",
  data() {
    return {
      selectedLocation: localStorage.getItem("selectedLocation") || "F",
      openKey: null,

      // Routes below are placeholders—swap to your real ones.
      rows: [
        {
          key: "planning",
          letter: "P",
          word: "lanning",
          options: [
            { label: "Finished Goods Scheduling", to: "/jim-scheduling" },
            { label: "WIP Scheduling", to: "/wip-scheduling" },
            { label: "CORE Scheduling", to: "/core-scheduling" },
            { label: "JKT Scheduling", to: "/jkt-scheduling" },
            { label: "PNT Scheduling", to: "/pnt-scheduling" },
          ],
        },
        {
          key: "analysis",
          letter: "A",
          word: "nalysis",
          options: [
            { label: "Demand & Forecast", to: "/purchasing-forecast" },
            { label: "Footage Shipped", to: "/footage-shipped-report" },
            { label: "Profit Analysis", to: "/analysis/menu" },  // ✅ NEW
            { label: "Financials", to: "/pnl" },  // ✅ NEW
            { label: "What-If", to: "/whatif" },  // ✅ NEW
            { label: "JIM Capacity What-Ifs", to: "/jim-capacity-whatifs" },  // ✅ NEW
            { label: "Freight", to: "/freight-analysis" },
            { label: "ITR Data", to: "/inventory-trend-report" },
            { label: "Inv Value", to: "/inventory-value-report" },
            { label: "Stock Status", to: "/inventory-stock-status" },
            { label: "JIM ROI", to: "/jim-capital-roi" },
            { label: "Tariff Exposure", to: "/tariff-exposure" },
            { label: "Open Orders", to: "/open-orders" },
            
          ],
        },
        {
          key: "buying",
          letter: "B",
          word: "uying",
          options: [
            { label: "Buying Page", to: "/buying" },
            { label: "Raw Materials Needed", to: "/material-alerts" },
            { label: "WIP Materials Needed", to: "/wip-material-alerts" },
            { label: "Vendors", to: "/vendors" },
          ],
        },
        {
          key: "logistics",
          letter: "L",
          word: "ogistics",
          options: [
            { label: "Schedule Trucks", to: "/logistics" },
            { label: "Nearest Distributors", to: "/nearest-distributors" },
          ],
        },
        {
          key: "operations",
          letter: "O",
          word: "perations",
          options: [
            { label: "Company Maintenance", to: "/company-maintenance" },
            { label: "Labels", to: "/gtin-labels" },
            { label: "Notes / Exceptions", to: "/ops-notes" },

            // ✅ NEW
            { label: "Pricing", to: "/operations/pricing" },
          ],
        },

      ],
    };
  },

  computed: {
  hasFinanceViewPerm() {
    const perms = JSON.parse(localStorage.getItem("perms") || "[]");
    return perms.includes("finance:view");
  },

  filteredRows() {
    return this.rows.map(row => {
      if (row.key !== "analysis") return row;

      const financeProtectedRoutes = new Set([
        "/pnl",
        "/jim-capacity-whatifs",
      ]);

      return {
        ...row,
        options: row.options.filter(opt => {
          if (!financeProtectedRoutes.has(opt.to)) return true;
          return this.hasFinanceViewPerm;
        }),
      };
    });
  },
},


  methods: {
    toggleRow(key) {
      this.openKey = this.openKey === key ? null : key;
    },
    saveLocation() {
      localStorage.setItem("selectedLocation", this.selectedLocation);
      // If you already emit/store location elsewhere, keep that logic too.
      // Example:
      // this.$emit("location-changed", this.selectedLocation);
    },
  },
};
</script>

<style scoped>
/* Page shell: match your darker Pablo vibe */
/* Page shell: dark Pablo vibe */
.pablo-dashboard {
  height: 100vh;
  width: 100%;
  background-color: #0b0101;
  color: #cbbfbf;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: Garamond, serif;
  position: relative;
  overflow: hidden;
}

/* Header */
.header {
  margin-top: 60px;
  text-align: center;
}

.page-heading {
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 60px;
  color: #cbbfbf;
  margin: 0 0 6px 0;
  letter-spacing: 2px;
}

.subtitle {
  font-size: 26px;
  opacity: 0.85;
  margin-bottom: 60px;
}

/* Menu container */
.menu {
  width: 100%;
  max-width: 900px;
  margin-top: 30px;
  padding: 0 14px 30px 14px;
}

/* ✅ Each PABLO row now feels like a Machine Row (slate/blue-gray) */
.row {
  background-color: #5595b7c2;              /* from machine-row */
  border: 1px solid rgba(203, 191, 191, 0.28);
  border-radius: 12px;
  margin-bottom: 14px;
  overflow: hidden;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);

  /* row-by-row animation */
  opacity: 0;
  transform: translateY(10px);
  animation: rowIn 220ms ease-out forwards;
}

@keyframes rowIn {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Hover like machine-row hover */
.row:hover {
  background-color: #5595b7c2;
  border-color: rgba(203, 191, 191, 0.38);
}

.row-header {
  width: 100%;
  background: transparent;
  border: none;
  color: inherit;
  padding: 16px 18px;
  display: flex;
  align-items: baseline;
  gap: 10px;
  cursor: pointer;
  text-align: left;
}

/* Subtle hover highlight (keeps your old behavior) */
.row-header:hover {
  background-color: rgba(255, 255, 255, 0.05);
}



/* P/A/B/L/O styling */
.big-letter {
  font-family: 'Cormorant Garamond', Garamond, serif;
  font-size: 44px;
  line-height: 1;
  color: #cbbfbf;
  min-width: 0px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.4);
}

.word {
  font-size: 28px;
  letter-spacing: 2px;
  color: #f9fafb;
  opacity: 0.95;
  margin-left: -6px;
}

.chev {
  margin-left: auto;
  opacity: 0.95;
  font-size: 18px;
  color: #cbbfbf;
}

/* ✅ Expanded body = Unscheduled green wash */
.row-body {
  border-top: 1px solid rgba(203, 191, 191, 0.18);
  padding: 14px 18px 18px 18px;

  background-color: #7affe216;             /* derived from #7affe276, slightly calmer */
  backdrop-filter: blur(1px);
  margin-top: 30px;
}

/* Options grid */
.option-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

/* ✅ Option pills = Unscheduled work-order cards */
.option {
  display: inline-block;
  text-decoration: none;

  background-color: #7affe276;             /* from unscheduled work-order */
  color: #111;                              /* readable on light card */

  border: 1px solid rgba(0, 0, 0, 0.18);
  border-radius: 10px;
  padding: 10px 12px;

  font-weight: 700;
  font-size: 14px;

  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.18);
  transition: transform 0.12s ease, background-color 0.12s ease, box-shadow 0.12s ease;
}

.option:hover {
  transform: translateY(-2px);
  background-color: #5595b7c2;            /* your Pablo teal/blue */
  color: #0b0101;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.28);
}

/* Smooth expand/collapse */
.expand-enter-active,
.expand-leave-active {
  transition: max-height 180ms ease, opacity 180ms ease;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
}

.expand-enter-to,
.expand-leave-from {
  max-height: 350px;
  opacity: 1;
}

</style>
